/**
 * PipelineStage Component
 * Individual stage in the assessment pipeline with circle, count, and label
 */

import type { LucideIcon } from "lucide-react";

export type StageType =
  | "intake_form"
  | "information_gathering"
  | "generating_results"
  | "review_results"
  | "completed"
  | "continuous_monitoring";

export interface PipelineStageProps {
  stageType: StageType;
  count: number;
  label: string;
  icon: LucideIcon;
  isFirst?: boolean;
  isLast?: boolean;
  onClick?: () => void;
}

// Stage color configurations
const stageColors: Record<StageType, { border: string; text: string }> = {
  intake_form: {
    border: "border-[#F0FCFF]",
    text: "text-[#09AACD]",
  },
  information_gathering: {
    border: "border-[#FFEDFA]",
    text: "text-[#992F7B]",
  },
  generating_results: {
    border: "border-[#E6F1FE]",
    text: "text-[#006FEE]",
  },
  review_results: {
    border: "border-[#FEF6EA]",
    text: "text-[#F5A524]",
  },
  completed: {
    border: "border-[#E9FAF1]",
    text: "text-[#1FCB6A]",
  },
  continuous_monitoring: {
    border: "border-[#F1F5F9]",
    text: "text-[#62748E]",
  },
};

export const PipelineStage = ({
  stageType,
  count,
  label,
  icon: Icon,
  isFirst = false,
  isLast = false,
  onClick,
}: PipelineStageProps) => {
  const colors = stageColors[stageType];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 flex-1">
      {/* Circle Row with Lines */}
      <div className="flex flex-row items-center w-full">
        {/* Left line */}
        {!isFirst && <div className="flex-1 h-px bg-[#99C7FB]" />}
        {isFirst && <div className="flex-1" />}

        {/* Circle */}
        <div
          className={`flex items-center justify-center w-12 h-12 mx-3 bg-white border-2 ${colors.border} rounded-full shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)] ${
            onClick ? "cursor-pointer hover:opacity-80 transition-opacity" : ""
          }`}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          role={onClick ? "button" : undefined}
          tabIndex={onClick ? 0 : undefined}
        >
          <span className={`font-inter font-medium text-lg leading-7 text-center ${colors.text}`}>{count}</span>
        </div>

        {/* Right line */}
        {!isLast && <div className="flex-1 h-px bg-[#99C7FB]" />}
        {isLast && <div className="flex-1" />}
      </div>

      {/* Label */}
      <div className="flex flex-row justify-center items-center gap-2">
        <Icon size={16} className="text-[#62748E]" />
        <span className="font-inter font-medium text-xs leading-5 text-[#62748E]">{label}</span>
      </div>
    </div>
  );
};

export default PipelineStage;
